import React, { useState } from "react";
import { CalendarClock, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { track } from "../lib/analytics";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const SLOTS = [
  { value: "morning", label: "Morning (9 AM – 12 PM)" },
  { value: "afternoon", label: "Afternoon (12 – 4 PM)" },
  { value: "evening", label: "Evening (4 – 8 PM)" },
];

export function BookingDialog({ prefill = {}, leadId, assessmentId, triggerClassName, triggerLabel = "Book a Meeting" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: prefill.full_name || "",
    mobile: prefill.mobile || "",
    email: prefill.email || "",
    preferred_date: "",
    time_slot: "morning",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const today = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Please enter your name.");
    if (!/^[0-9+\-\s]{7,15}$/.test(form.mobile.trim())) return toast.error("Please enter a valid mobile number.");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Please enter a valid email.");
    if (!form.preferred_date) return toast.error("Please pick a preferred date.");
    setSubmitting(true);
    try {
      await api.post("/meetings", { ...form, lead_id: leadId || null, assessment_id: assessmentId || null });
      track("consultation_requested", { slot: form.time_slot });
      toast.success("Meeting request sent! We'll confirm shortly.");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send request.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="cta-book" className={triggerClassName}>
          <CalendarClock size={18} /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy flex items-center gap-2"><Phone size={18} className="text-gold" /> Book a Financial Planning Call</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Full Name *</Label>
            <Input data-testid="book-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5 rounded-lg" placeholder="Your name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Mobile *</Label>
              <Input data-testid="book-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1.5 rounded-lg" placeholder="Mobile" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Email *</Label>
              <Input data-testid="book-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-lg" placeholder="Email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Preferred Date *</Label>
              <Input data-testid="book-date" type="date" min={today} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Time Slot</Label>
              <select data-testid="book-slot" value={form.time_slot} onChange={(e) => set("time_slot", e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 h-10 px-3 text-sm bg-white">
                {SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Note (optional)</Label>
            <Textarea data-testid="book-note" value={form.note} onChange={(e) => set("note", e.target.value)} className="mt-1.5 rounded-lg" rows={2} placeholder="Anything you'd like to discuss?" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="book-submit" onClick={submit} disabled={submitting}
            className="w-full rounded-full bg-navy text-white font-heading font-bold py-3 flex items-center justify-center gap-2 hover:bg-royal transition-colors duration-300 disabled:opacity-60">
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <>Request Meeting</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
